import type { BenefitCellValue, BenefitItem } from 'src/types/Changelog';
import type {
	CameraCloudStorageBenefit,
	CameraCloudStorageBenefitValue,
	FormatType,
	PremiumBenefit,
} from 'src/types/Subscriptions';

export function getPremiumBenefitsList(format: FormatType): PremiumBenefit[] {
	const benefits = [
		{
			text: format('text_premium_benefits_camera_cloud_storage', {
				default: 'Camera Cloud Storage',
			}),
			info: format('info_premium_benefits_camera_cloud_storage', {
				default:
					"You'll be granted with 1 free camera cloud storage slot to store your camera recordings.",
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				details: format('label_camera_slot', {
					default: 'camera slot',
				}),
				detailsValue: 1,
			},
			comingSoon: false,
			new: true,
		},
		{
			text: format('ai_chat_title', {
				default: 'Shelly Assistant',
			}),
			info: format('text_premium_benefits_shelly_assistant_subheader', {
				default: "Ask about your devices' status and set up scenes effortlessly.",
			}),
			basicSubscription: {
				details: format('prompts_per_day', {
					default: 'prompts / day',
				}),
				detailsValue: 10,
			},
			premiumSubscription: {
				details: format('prompts_per_day', {
					default: 'prompts / day',
				}),
				detailsValue: 50,
			},
			comingSoon: false,
			isAI: true,
			new: true,
		},
		{
			text: format('text_premium_benefits_failure_detection_header', {
				default: 'AI models for device failure detection',
			}),
			info: format('text_premium_benefits_failure_detection_subheader', {
				default:
					'Get notified in case of abnormal device behaviour based on energy consumption.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				details: format('ai_models', {
					default: 'AI Models',
				}),
				detailsValue: 10,
			},
			comingSoon: false,
			isAI: true,
		},
		{
			text: format('text_premium_benefits_offline_device_notification_header', {
				default: 'Offline device notification',
			}),
			info: format('text_premium_benefits_offline_device_notification_subheader', {
				default: 'Receive notifications in case of offline Shelly device.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			comingSoon: false,
			isAI: false,
		},
		{
			text: format('text_premium_benefits_virtual_devices_header', {
				default: 'Virtual devices',
			}),
			info: format('text_premium_benefits_virtual_devices_subheader', {
				default: 'Extract your virtual components into virtual devices',
			}),
			basicSubscription: {
				details: format('text_premium_benefits_virtual_device_header', {
					default: 'virtual device',
				}),
				detailsValue: 1,
			},
			premiumSubscription: {
				details: format('text_premium_benefits_virtual_devices_header', {
					default: 'virtual devices',
				}),
				detailsValue: 20,
			},
			comingSoon: false,
			isAI: false,
			isNotPremiumOnly: true,
		},
		{
			text: format('text_premium_benefit_history_event_log', {
				default: 'Extended History Event Log',
			}),
			info: format('text_premium_benefits_event_log_subheader', {
				default: 'Get access to up to 100 events per device.',
			}),
			basicSubscription: {
				details: format('text_premium_subs_basic_events_count', {
					default: 'events',
				}),
				detailsValue: 5,
			},
			premiumSubscription: {
				details: format('text_premium_subs_premium_events_count', {
					default: 'events',
				}),
				detailsValue: 100,
			},
			betaFlag: false,
		},
		{
			text: format('text_premium_benefit_statistics_header', {
				default: 'Extended Statistics Data Visualization',
			}),
			info: format('text_premium_benefit_statistics_subheader', {
				default: 'Track all your data in 1-minute intervals.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			betaFlag: false,
		},
		{
			text: format('text_premium_benefits_alexa_header', {
				default: 'Additional Alexa Virtual Actions',
			}),
			info: format('text_premium_benefits_alexa_subheader', {
				default: 'Create up to 100 Alexa virtual actions.',
			}),
			basicSubscription: {
				details: format('tab_scenes', {
					default: 'scenes',
					values: { detailsValue: 3 },
				}),
				detailsValue: 3,
			},
			premiumSubscription: {
				details: format('tab_scenes', {
					default: 'scenes',
					values: { detailsValue: 100 },
				}),
				detailsValue: 100,
			},
			betaFlag: false,
		},
		{
			text: format('text_premium_benefits_weather_widget_header', {
				default: 'Weather Information Widget',
			}),
			info: format('text_premium_benefits_weather_widget_subheader', {
				default: 'Access detailed weather information customised to your chosen location.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			betaFlag: false,
		},
		{
			text: format('text_premium_benefits_weather_scene_header', {
				default: 'Weather-forecast Scene Execution',
			}),
			info: format('text_premium_benefits_weather_scene_subheader', {
				default: 'Create scenes that respond to the weather forecast.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			betaFlag: false,
		},
		{
			text: format('text_premium_benefits_forgotten_lights_header', {
				default: 'Forgotten lights remainder',
			}),
			info: format('text_premium_benefits_forgotten_lights_info_subheader', {
				default: 'Receive notifications in case of forgotten lights.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
		},
		{
			text: format('text_premium_benefits_dashboards_header', {
				default: 'Additional dashboards',
			}),
			info: format('text_premium_benefits_dashboards_subheader', {
				default: 'Create up to 20 custom dashboards.',
			}),
			basicSubscription: {
				details: format('text_premium_benefits_basic_dashes_count', {
					default: 'dashes',
				}),
				detailsValue: 5,
			},
			premiumSubscription: {
				details: format('text_premium_benefits_premium_dashes_count', {
					default: 'dashes',
				}),
				detailsValue: 20,
			},
		},
		{
			text: format('text_premium_benefits_widgets_header', {
				default: 'Additional Widgets',
			}),
			info: format('text_premium_benefits_widgets_subheader', {
				default: 'Set up to 80 custom widgets per dashboard.',
			}),
			basicSubscription: {
				details: format('text_premium_benefits_basic_widget_count', {
					default: 'widgets',
				}),
				detailsValue: 40,
			},
			premiumSubscription: {
				details: format('text_premium_benefits_premium_widget_count', {
					default: 'widgets',
				}),
				detailsValue: 80,
			},
		},
		{
			text: format('text_premium_benefits_mounthy_reports_header', {
				default: 'Monthly energy reports',
			}),
			info: format('text_premium_benefits_mounthy_reports_subheader', {
				default:
					'Get monthly energy reports within the app and have them delivered to your email.',
			}),
			basicSubscription: {
				icon: 'fa-solid fa-xmark',
			},
			premiumSubscription: {
				icon: 'fa-solid fa-check',
			},
			comingSoon: false,
		},
	];

	return benefits.filter((benefit): benefit is PremiumBenefit => Boolean(benefit));
}

export const cameraCloudStorageBenefits: CameraCloudStorageBenefit[] = [
	{
		title: 'text_camera_cloud_storage_benefit_live_streaming_header',
		titleDefault: 'Shelly Camera Live Streaming',
		subtitle: 'text_camera_cloud_storage_benefit_live_streaming_subheader',
		subtitleDefault: 'Stream live video from the camera whenever needed.',
		standard: true,
		cameraStorage: true,
	},
	{
		title: 'text_camera_cloud_storage_benefit_monitoring_center_header',
		titleDefault: '24/7 monitoring center',
		subtitle: 'text_camera_cloud_storage_benefit_monitoring_center_subheader',
		subtitleDefault: 'A single monitoring center covering all your cameras, around the clock.',
		standard: true,
		cameraStorage: true,
	},
	{
		title: 'text_camera_cloud_storage_benefit_privacy_design_header',
		titleDefault: 'Privacy by design',
		subtitle: 'text_camera_cloud_storage_benefit_privacy_design_subheader',
		subtitleDefault:
			'Built with privacy-by-design principles, including data minimization and EU-grade protections.',
		standard: true,
		cameraStorage: true,
	},
	{
		title: 'text_camera_cloud_storage_benefit_privacy_zones_header',
		titleDefault: 'Fully customizable privacy zones',
		subtitle: 'text_camera_cloud_storage_benefit_privacy_zones_subheader',
		subtitleDefault: 'Ensure the privacy of your neighbors or the captures of specific zones.',
		standard: true,
		cameraStorage: true,
	},
	{
		title: 'text_camera_cloud_storage_benefit_backup_header',
		titleDefault: 'Secure, off-site video backup',
		subtitle: 'text_camera_cloud_storage_benefit_backup_subheader',
		subtitleDefault:
			'Videos are stored safely in the cloud and stay accessible even if the device is lost or offline.',
		standard: false,
		cameraStorage: true,
	},
	{
		title: 'text_camera_cloud_storage_benefit_video_history_header',
		titleDefault: '30 days of video and image history',
		subtitle: 'text_camera_cloud_storage_benefit_video_history_subheader',
		subtitleDefault: 'Browse and review up to 30 days of clips and snapshots on demand.',
		standard: false,
		cameraStorage: true,
	},
	{
		title: 'text_camera_cloud_storage_benefit_encryption_header',
		titleDefault: 'Video Encryption',
		subtitle: 'text_camera_cloud_storage_benefit_encryption_subheader',
		subtitleDefault:
			'Every video is encrypted with a device-specific key, unlocked only by a password kept off the cloud.',
		standard: false,
		cameraStorage: true,
	},
	{
		title: 'text_camera_cloud_storage_benefit_image_preview_header',
		titleDefault: 'Image preview',
		subtitle: 'text_camera_cloud_storage_benefit_image_preview_subheader',
		subtitleDefault: 'A snapshot of what the camera sees.',
		standard: {
			label: 'text_camera_cloud_storage_snapshot_recent_event',
			labelDefault: 'Snapshot of recent event',
			icon: true,
		},
		cameraStorage: {
			label: 'text_camera_cloud_storage_snapshot_every_capture',
			labelDefault: 'Snapshot on every capture or motion',
			icon: true,
		},
	},
	{
		title: 'activate_storage_benefit_ai_title',
		titleDefault: 'AI object recognition',
		subtitle: 'activate_storage_benefit_ai_desc',
		subtitleDefault: 'Turn the detected person, pet, or vehicles into smarter Scenes automation.',
		standard: false,
		cameraStorage: true,
	},
	{
		title: 'text_camera_cloud_storage_benefit_event_tracking_header',
		titleDefault: '24/7 event tracking',
		subtitle: 'text_camera_cloud_storage_benefit_event_tracking_subheader',
		subtitleDefault: 'Continuous capture of motion events, privacy toggles, and alerts.',
		standard: {
			value: '5',
			label: 'text_camera_cloud_storage_events',
			labelDefault: 'events',
		},
		cameraStorage: {
			value: '100',
			label: 'text_camera_cloud_storage_events',
			labelDefault: 'events',
		},
	},
	{
		title: 'text_camera_cloud_storage_benefit_motion_recording_header',
		titleDefault: 'Motion events with recording',
		subtitle: 'text_camera_cloud_storage_benefit_motion_recording_subheader',
		subtitleDefault:
			'Get the full picture when it matters most, with video backing your key motion events.',
		standard: false,
		cameraStorage: true,
	},
	{
		title: 'text_camera_cloud_storage_benefit_notifications_header',
		titleDefault: 'Notifications straight to your phone',
		subtitle: 'text_camera_cloud_storage_benefit_notifications_subheader',
		subtitleDefault: 'Get notified for every motion detected',
		standard: {
			label: 'text_camera_cloud_storage_text_alert',
			labelDefault: 'Text alert',
			icon: true,
		},
		cameraStorage: {
			label: 'text_camera_cloud_storage_text_alert_with_photo',
			labelDefault: 'Text alert plus event photo',
			icon: true,
		},
	},
];

const PREMIUM_CHECK_ICON = 'fa-solid fa-check';
const PREMIUM_CROSS_ICON = 'fa-solid fa-xmark';

/**
 * Splits CMS value labels like "10 prompts / day" into the two-line
 * number + unit shape the premium comparison table already renders.
 */
export function splitBenefitValueLabel(label: string): {
	details?: string;
	detailsValue?: number;
} {
	const trimmed = label.trim();
	if (!trimmed) return {};

	const match = trimmed.match(/^(\d+(?:\.\d+)?)\s+(.+)$/);
	if (match) {
		return {
			detailsValue: Number(match[1]),
			details: match[2],
		};
	}

	return { details: trimmed };
}

const resolveCellLabel = (value: BenefitCellValue, translatedLabel: string | null): string | null =>
	translatedLabel || value.label || null;

const mapPremiumCell = (
	value: BenefitCellValue,
	translatedLabel: string | null,
): PremiumBenefit['basicSubscription'] => {
	const label = resolveCellLabel(value, translatedLabel);

	if (value.type === 'cross') {
		return { icon: PREMIUM_CROSS_ICON };
	}

	if (value.type === 'check' && !label) {
		return { icon: PREMIUM_CHECK_ICON };
	}

	const split = splitBenefitValueLabel(label ?? '');
	if (split.details || typeof split.detailsValue !== 'undefined') {
		return split;
	}

	return { icon: value.type === 'check' ? PREMIUM_CHECK_ICON : PREMIUM_CROSS_ICON };
};

const hasBadge = (badges: string[], badge: string): boolean =>
	badges.some((item) => item.toLowerCase() === badge);

/**
 * Maps a CMS benefits list onto the Premium comparison-table shape.
 */
export function mapCmsItemsToPremiumBenefits(items: BenefitItem[]): PremiumBenefit[] {
	return items.map((item) => ({
		text: item.translation.title,
		info: item.translation.description,
		basicSubscription: mapPremiumCell(item.standardValue, item.translation.standardValueLabel),
		premiumSubscription: mapPremiumCell(item.featuredValue, item.translation.featuredValueLabel),
		new: hasBadge(item.badges, 'new'),
		isAI: hasBadge(item.badges, 'ai'),
		betaFlag: hasBadge(item.badges, 'beta'),
		comingSoon: hasBadge(item.badges, 'coming_soon') || hasBadge(item.badges, 'comingsoon'),
	}));
}

const mapCameraCloudStorageCell = (
	value: BenefitCellValue,
	translatedLabel: string | null,
): CameraCloudStorageBenefitValue => {
	const label = resolveCellLabel(value, translatedLabel);

	if (value.type === 'cross') return false;
	if (value.type === 'check' && !label) return true;

	if (value.type === 'check' && label) {
		return {
			icon: true,
			label,
			labelDefault: label,
		};
	}

	if (!label) return false;

	const split = splitBenefitValueLabel(label);
	if (typeof split.detailsValue !== 'undefined' && split.details) {
		return {
			value: String(split.detailsValue),
			label: split.details,
			labelDefault: split.details,
		};
	}

	return {
		label,
		labelDefault: label,
	};
};

/**
 * Maps a CMS benefits list onto the Camera Cloud Storage comparison-table shape.
 */
export function mapCmsItemsToCameraCloudStorageBenefits(
	items: BenefitItem[],
): CameraCloudStorageBenefit[] {
	return items.map((item) => ({
		title: item.translation.title,
		titleDefault: item.translation.title,
		subtitle: item.translation.description,
		subtitleDefault: item.translation.description,
		standard: mapCameraCloudStorageCell(item.standardValue, item.translation.standardValueLabel),
		cameraStorage: mapCameraCloudStorageCell(
			item.featuredValue,
			item.translation.featuredValueLabel,
		),
		new: hasBadge(item.badges, 'new'),
		isAI: hasBadge(item.badges, 'ai'),
		betaFlag: hasBadge(item.badges, 'beta'),
		comingSoon: hasBadge(item.badges, 'coming_soon') || hasBadge(item.badges, 'comingsoon'),
	}));
}
